/**
 * `/inventory` hub layout — one sidebar item for the whole blank-inventory lifecycle. An h1 + one-line
 * intro, the global actions (Count the shelf / Restock / Refresh), a four-tile KPI strip, and the
 * section tabs in TWO LABELED GROUPS — Blanks (On hand / Purchase draft / Inbound / Receiving) and
 * Other stock (Printed transfers / Stock items). The former "Short & reorder" tab merged into On hand
 * (chips); its red badge moved with it. KPI counts + tab badges come from the single-walk
 * GET /inventory/overview (shared query key, fetched once); the "Reorder cost" KPI additionally reads
 * the draft PO and is admin-only (cost is requireCostView-gated).
 *
 * The Other-stock tabs are separate ledgers (films / Ship-only goods, not blanks): the blank KPI
 * strip and the blank actions (Count the shelf, Restock) hide there, but the page header STAYS and
 * the intro line switches to the group's own identity — the old behavior unmounted all chrome, which
 * read as the page breaking (audit 2026-08-08).
 *
 * Tabs are real <Link>s (deep-linkable, back/forward works). Active styling keys off the
 * `data-status="active"` attribute TanStack sets on the active link. The On-hand tab matches exactly
 * so it isn't lit on the sub-paths.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { AlertTriangle, ClipboardList, PackagePlus, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PAGE_CONTAINER_CLASS } from '@/components/page-container';
import { Button } from '@/components/ui/button';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import { useAuth } from '@/lib/auth-state';
import { cn } from '@/lib/utils';
import { AddStockDialog } from './add-stock-dialog';
import { buildCountSheet, flattenSheet, hubPullItems } from './count-shelf-lib';
import { invalidateInventory } from './inventory-lib';

type InventoryOverview = components['schemas']['InventoryOverview'];
type ReceivingShortages = components['schemas']['ReceivingShortagesResult'];

type Tab = { label: string; to: string; exact?: boolean; badge?: number };

const TAB_CLASS =
  'flex shrink-0 items-center border-b-2 border-transparent px-3 py-2.5 text-sm font-medium ' +
  'text-muted-foreground transition-colors hover:text-foreground data-[status=active]:border-primary ' +
  'data-[status=active]:font-semibold data-[status=active]:text-foreground';

export function InventoryHubLayout() {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  // Cosmetic gate mirroring the server (admin || inventory-admin) — also the requireCostView group,
  // so it doubles as the cost-KPI gate. The server 403 stays the real gate.
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;
  const [addOpen, setAddOpen] = useState(false);
  // The Other-stock tabs are separate ledgers (films / Ship-only goods, not blanks) — the blank KPI
  // strip and the blank actions don't apply there; the header stays, with its own identity line.
  const onOtherStock = useLocation({
    select: (l) => l.pathname === '/inventory/transfers' || l.pathname === '/inventory/items',
  });
  // Receiving is a tool used standing at an open box, often on a phone: the four KPI tiles above it
  // pushed the scan field a full screen down (measured 673px at 390px wide). On that tab the strip
  // collapses to one line of pills, hidden on phones — the numbers are one tap away on any other tab.
  const onReceiving = useLocation({ select: (l) => l.pathname === '/inventory/receiving' });

  const overview = useQuery({
    queryKey: ['inventory-overview'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/overview');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the overview.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // The blanks list feeds the Add-stock dialog's on-hand-per-size preview (shared cache with On hand).
  const inventory = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inventory.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items;
    },
  });

  // Inbound board — only the "delivered but not yet received" count is surfaced here (a tab alert badge,
  // so a delivered box can't fall through while the Inbound tab is closed). Shared query key with the
  // Inbound page; staleTime so a hub visit / refocus doesn't re-hit S&S.
  const inbound = useQuery({
    queryKey: ['inventory-inbound'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/inbound');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load inbound.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Open "S&S owes us" claims — money the supplier owes, surfaced at hub level at last (P3, F5-3):
  // a red Receiving badge + a KPI tile, so the count is visible without scrolling the Receiving tab.
  // Shares the Receiving page's query key; the change feed maps 'receiving' to the inventory domain.
  const claims = useQuery({
    queryKey: ['receiving', 'shortages'],
    queryFn: async () => {
      const { data, error } = await client.GET('/receiving/shortages');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load claims.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Pulled blanks (v2 B2): the three pull items under Count the shelf — a sheet whose pull started but
  // was never confirmed, a closed sheet with blanks still out for days, a short recorded at the pull
  // without its shelf answer. Read off the count sheet under the frozen `count-shelf` root's own
  // sub-key (short-lived, refreshed by the hub's Refresh); hidden on the Other-stock tabs.
  const pulled = useQuery({
    queryKey: ['count-shelf', 'hub'],
    enabled: !onOtherStock,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await client.GET('/inventory/count-sheet');
      if (res.response?.status === 404) return null; // an old server: no items to show
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to read the pull items.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
  });
  const pullItems = useMemo(
    () =>
      pulled.data && !pulled.data.degraded
        ? hubPullItems(flattenSheet(buildCountSheet(pulled.data.rows)), Date.now())
        : null,
    [pulled.data],
  );

  const ov = overview.data;
  // RED tab badge = "a customer order cannot be filled", nothing softer. Since 2026-08-12
  // `belowThreshold` also fires on a blank sitting AT its par whose free stock (net of committed
  // orders) is short — a purchasing signal that would roughly 5x this count and leave the tab
  // permanently red. It keeps its own KPI tile and its own amber chip instead.
  const shortCount = ov ? ov.rows.filter((r) => r.shortage > 0).length : undefined;
  const claimCount = claims.data?.shortages.length;
  const fetching = overview.isFetching || inventory.isFetching || inbound.isFetching;
  // Tab badges are reserved for RED action counts (P3, F5-7) — one badge per distinct action: the
  // short/below count on the merged On-hand tab (its chips answer it), delivered-awaiting on Inbound,
  // open claims on Receiving. Purchase draft carries none (its number = the Below-reorder-level KPI).
  const blankTabs: Tab[] = [
    { label: 'On hand', to: '/inventory', exact: true, badge: shortCount },
    { label: 'Purchase draft', to: '/inventory/purchase-draft' },
    { label: 'Inbound', to: '/inventory/inbound', badge: inbound.data?.kpis.awaitingReceiptCount },
    { label: 'Receiving', to: '/inventory/receiving', badge: claimCount },
  ];
  const otherTabs: Tab[] = [
    { label: 'Printed transfers', to: '/inventory/transfers' },
    { label: 'Printed garments', to: '/inventory/garments' },
    { label: 'Stock items', to: '/inventory/items' },
  ];

  return (
    <div className={cn(PAGE_CONTAINER_CLASS, 'space-y-5')}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold tracking-tight">Inventory</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {onOtherStock
              ? 'Films and finished goods — separate from blanks, counted the same way.'
              : "Blanks on hand, what's short, what to reorder, and receiving — all in one place."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!onOtherStock && (
            <Button size="sm" asChild>
              {/* The physical walk: anchor on-hand to what is actually on the shelf. */}
              <Link to="/count">
                <ClipboardList className="size-4" />
                Count the shelf
              </Link>
            </Button>
          )}
          {!onOtherStock && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              disabled={!canWrite}
              title={canWrite ? undefined : 'Admins only'}
            >
              <PackagePlus className="size-4" />
              Restock
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              invalidateInventory(qc);
              qc.invalidateQueries({ queryKey: ['count-shelf', 'hub'] });
            }}
            disabled={fetching}
          >
            <RefreshCw className={cn('size-4', fetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </header>

      {!onOtherStock && pullItems && <PullItems items={pullItems} />}

      {!onOtherStock && !onReceiving && (
        <InventoryKpis overview={ov} claims={claims.data} canSeeCost={canWrite} />
      )}
      {onReceiving && <CompactKpis overview={ov} claims={claims.data} />}

      <nav
        className="flex flex-wrap items-end gap-x-6 gap-y-0 border-b"
        aria-label="Inventory sections"
      >
        <TabGroup label="Blanks" tabs={blankTabs} />
        <TabGroup label="Other stock" tabs={otherTabs} />
      </nav>
      <Outlet />

      <AddStockDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existing={inventory.data ?? []}
      />
    </div>
  );
}

/**
 * The pull items (pulled-blanks v2 §5 Hub). One hint under Count the shelf: *"1 sheet's pull started but
 * not confirmed — #215"*; the alert for a closed sheet's blanks *still out* for more than a few days;
 * *"shelf short at the pull — 4 Tultex 602 Black XL on #219 — count it"* for a short recorded without
 * its shelf answer. Quiet when there is nothing. Each item links to where it is answered.
 */
function PullItems({ items }: { items: ReturnType<typeof hubPullItems> }) {
  if (items.unconfirmed.length === 0 && items.stillOut.length === 0 && items.shorts.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1 text-xs" data-testid="pull-items">
      {items.unconfirmed.length > 0 && (
        <p className="text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" />
          {items.unconfirmed.length === 1
            ? "1 sheet's pull started but not confirmed"
            : `${items.unconfirmed.length} sheets' pull started but not confirmed`}{' '}
          — {items.unconfirmed.map((u) => u.tag).join(', ')}.{' '}
          <Link to="/count" className="underline underline-offset-2">
            The count asks about it
          </Link>
          ; the puller ticks the rows from the sheet's card.
        </p>
      )}
      {items.stillOut.map((s) => (
        <p key={s.batchId} className="text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" />
          {s.out} blank{s.out === 1 ? '' : 's'} still out on closed {s.tag} for {s.days} day
          {s.days === 1 ? '' : 's'} — returned to the shelf?{' '}
          <Link
            to="/batches/$batchId"
            params={{ batchId: s.batchId }}
            className="underline underline-offset-2"
          >
            Open the sheet
          </Link>
        </p>
      ))}
      {items.shorts.map((x) => (
        <p key={`${x.ssSku}-${x.batchId}`} className="text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mr-1 inline size-3.5 align-[-2px]" />
          shelf short at the pull — {x.short} {x.title} {x.variant.replace(/ · /g, ' ')} on {x.tag}{' '}
          —{' '}
          <Link to="/count" search={{ focus: x.ssSku }} className="underline underline-offset-2">
            count it
          </Link>
        </p>
      ))}
    </div>
  );
}

/** One labeled segment of the tab bar. The groups wrap as units on phones — no more tabs hidden
 *  off the end of a horizontally-scrolling row with no affordance. */
function TabGroup({ label, tabs }: { label: string; tabs: Tab[] }) {
  return (
    <div className="flex items-end">
      <span
        className="hidden pb-2.5 pr-2 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground/70 sm:block"
        aria-hidden
      >
        {label}
      </span>
      {tabs.map((t) => (
        <Link
          key={t.to}
          to={t.to}
          activeOptions={t.exact ? { exact: true } : undefined}
          className={TAB_CLASS}
        >
          {t.label}
          {t.badge != null && t.badge > 0 && (
            <span className="ml-2 rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-red-700 dark:bg-red-950/60 dark:text-red-300">
              {t.badge}
            </span>
          )}
        </Link>
      ))}
    </div>
  );
}

/** The Receiving tab's one-line KPI strip (desktop only) — the same numbers, without the tiles. */
function CompactKpis({
  overview,
  claims,
}: {
  overview: InventoryOverview | undefined;
  claims: ReceivingShortages | undefined;
}) {
  if (!overview) return null;
  const k = overview.kpis;
  const claimCount = claims?.shortages.length ?? 0;
  const pill = (
    label: string,
    value: number,
    to: string,
    tone: 'danger' | 'warn' | undefined,
    search?: Record<string, string>,
  ) => (
    <Link
      to={to}
      search={search}
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          tone === 'danger' ? 'bg-red-500' : tone === 'warn' ? 'bg-amber-500' : 'bg-emerald-500',
        )}
        aria-hidden
      />
      {label}
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
    </Link>
  );
  return (
    <nav className="hidden flex-wrap gap-2 sm:flex" aria-label="Inventory at a glance">
      {pill('Short for orders', k.short, '/inventory', k.short > 0 ? 'danger' : undefined, {
        filter: 'short',
      })}
      {pill(
        'To reorder',
        k.belowPar,
        '/inventory/purchase-draft',
        k.belowPar > 0 ? 'warn' : undefined,
      )}
      {pill(
        'S&S claims',
        claimCount,
        '/inventory/receiving',
        claimCount > 0 ? 'danger' : undefined,
      )}
    </nav>
  );
}

/** At-a-glance counts, cut to the four ACTIONABLE tiles (audit 2026-08-08 — Blanks tracked was a
 *  vanity count; Uncounted/Negative folded into the On-hand filter chips). Every tile is a LINK to
 *  the view that explains its number, the cost tile included. */
function InventoryKpis({
  overview,
  claims,
  canSeeCost,
}: {
  overview: InventoryOverview | undefined;
  claims: ReceivingShortages | undefined;
  canSeeCost: boolean;
}) {
  // Stay quiet on the very first load / on error — the tab content surfaces its own error state, and a
  // half-empty KPI strip would read as real zeros.
  if (!overview) return null;
  const k = overview.kpis;
  const claimCount = claims?.shortages.length ?? 0;
  const claimUnits = claims?.totalShortUnits ?? 0;

  return (
    <div
      className={cn('grid grid-cols-2 gap-2.5', canSeeCost ? 'lg:grid-cols-4' : 'lg:grid-cols-3')}
    >
      <Kpi
        label="Short for orders"
        value={k.short}
        tone={k.short > 0 ? 'danger' : undefined}
        sub="open orders need more than we have"
        to="/inventory"
        search={{ filter: 'short' }}
      />
      <Kpi
        label="To reorder"
        value={k.belowPar}
        tone={k.belowPar > 0 ? 'warn' : undefined}
        sub="covers open orders + the shelf buffer"
        to="/inventory/purchase-draft"
      />
      <Kpi
        label="S&S claims"
        value={claimCount}
        tone={claimCount > 0 ? 'danger' : undefined}
        sub={claimCount > 0 ? `${claimUnits} pieces owed by S&S` : 'nothing owed by S&S'}
        to="/inventory/receiving"
      />
      {canSeeCost && <ReorderCostKpi />}
    </div>
  );
}

/**
 * Estimated reorder spend — the draft PO's total (Σ below-level lines × the deterministic S&S
 * effective cost). Admin-only (the caller gates on canSeeCost; the endpoint is requireCostView-gated
 * too). Its own query so it never blocks the count KPIs; staleTime so a hub revisit / refocus doesn't
 * re-hit S&S. No sparkline — there's no historical series to draw one from.
 */
function ReorderCostKpi() {
  const client = useApiClient();
  const q = useQuery({
    queryKey: ['inventory-purchase-draft'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/inventory/purchase-draft');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the draft PO.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });
  const draft = q.data;
  // Some lines may be unpriced (S&S blip / beyond the cap) → the total is a floor, shown as "≥".
  const incomplete = draft ? draft.unavailableCount + draft.skippedCount > 0 : false;
  const value = q.isPending
    ? '…'
    : draft
      ? `${incomplete ? '≥ ' : ''}$${Math.round(draft.estimatedTotal).toLocaleString('en-US')}`
      : '—';
  const lines = draft?.lines.length ?? 0;

  return (
    <Link
      to="/inventory/purchase-draft"
      className="block rounded-lg border bg-card p-3 shadow-sm transition-colors hover:border-primary/50 hover:bg-muted/40"
    >
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'size-1.5 shrink-0 rounded-full',
            draft && draft.estimatedTotal > 0 ? 'bg-amber-500' : 'bg-emerald-500',
          )}
          aria-hidden
        />
        <div className="text-xs font-medium text-muted-foreground">Reorder cost (est.)</div>
      </div>
      <div className="mt-0.5 text-2xl font-semibold tabular-nums tracking-tight">{value}</div>
      <div className="text-[11px] text-muted-foreground">
        {draft
          ? `${lines} draft ${lines === 1 ? 'line' : 'lines'}${incomplete ? ' · partial est.' : ''}`
          : 'from the Purchase draft'}
      </div>
    </Link>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
  to,
  search,
}: {
  label: string;
  value: number;
  sub: string;
  tone?: 'danger' | 'warn';
  /** Where the number explains itself — every tile links to its filtered view. */
  to: string;
  /** Optional search params for the link (e.g. the On-hand chip presets). */
  search?: Record<string, string>;
}) {
  return (
    <Link
      to={to}
      search={search}
      className="block rounded-lg border bg-card p-3 shadow-sm transition-colors hover:border-primary/50 hover:bg-muted/40"
    >
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'size-1.5 shrink-0 rounded-full',
            tone === 'danger' ? 'bg-red-500' : tone === 'warn' ? 'bg-amber-500' : 'bg-emerald-500',
          )}
          aria-hidden
        />
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
      </div>
      <div
        className={cn(
          'mt-0.5 text-2xl font-semibold tabular-nums tracking-tight',
          tone === 'danger' && 'text-red-700 dark:text-red-400',
          tone === 'warn' && 'text-amber-700 dark:text-amber-400',
        )}
      >
        {value}
      </div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </Link>
  );
}
